import cn from 'classnames';
import * as React from 'react';
import styles from './VisuallyHidden.module.scss';

// given an intrinsic element, returns its type.  for example,
// GetElement<'span'> === HTMLSpanElement
type GetElement<E> = E extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[E] extends React.HTMLAttributes<infer U>
    ? U
    : never
  : never;

// given an intrinsic element, returns its props. for example,
// GetElementProps<'audio'> === AudioHTMLAttributes<HTMLAudioElement>
type GetElementProps<E> = E extends keyof JSX.IntrinsicElements
  ? JSX.IntrinsicElements[E] extends React.DetailedHTMLProps<infer U, unknown>
    ? U
    : never
  : never;

export type VisuallyHiddenProps<
  E extends keyof JSX.IntrinsicElements = 'span',
> = GetElementProps<E> & {
  as?: E;
};

function VisuallyHiddenComponent<E extends keyof JSX.IntrinsicElements>(
  props: VisuallyHiddenProps<E>,
  ref: React.ForwardedRef<GetElement<E>>,
) {
  const Element: string = props.as ?? 'span';
  const { className } = props;

  return (
    <Element
      {...props}
      // error surfaced after upgrading typescript to latest 4.x.x
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      className={cn(styles.visuallyHidden, className)}
      ref={ref}
    />
  );
}

const VisuallyHidden = React.forwardRef(VisuallyHiddenComponent);

export default VisuallyHidden;
